import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TInputs = {
  brand?: {
    value: number;
    label: string;
  };
};

type TBrandOption = {
  value: number;
  label: string;
};

const defaultValues: TInputs = {
  brand: undefined,
};

export const CrmBrandFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  const { control, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Update active filter button
  useEffect(() => {
    if (params.brandId) setIsActive.on();
    else setIsActive.off();
  }, [params.brandId, setIsActive]);

  // Update values when params change
  useEffect(() => {
    if (params?.brandId) {
      setValue('brand', {
        value: Number(params.brandId),
        label: '', // Will be filled by async select loadOptions
      });
    } else {
      setValue('brand', undefined);
    }
  }, [params.brandId, setValue]);

  const onSubmit = (data: TInputs) => {
    if (data.brand?.value) {
      params.brandId = data.brand.value;
    } else {
      delete params.brandId;
    }
    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    // Reset form to current param values
    if (params?.brandId) {
      setValue('brand', {
        value: Number(params.brandId),
        label: '',
      });
    } else {
      setValue('brand', undefined);
    }
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  // Async loadOptions for ChakraAsyncSelect
  const loadBrandOptions = async (inputValue: string): Promise<TBrandOption[]> => {
    try {
      const { data } = await http.get('ums/v1/crm/management/brands', {
        params: {
          page: 1,
          limit: 50,
          name: inputValue || undefined,
        },
      });
      return (data?.data || []).map((brand: any) => ({
        value: brand.id,
        label: brand.name,
      }));
    } catch (error) {
      console.error('Error loading brands:', error);
      return [];
    }
  };

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='brand'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>Brand</FormLabel>
                <ChakraAsyncSelect
                  id='brand'
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder='Type to search and select brand...'
                  isClearable
                  isSearchable
                  loadOptions={loadBrandOptions}
                  defaultOptions
                  cacheOptions
                  getOptionLabel={(option: any) => option.label}
                  getOptionValue={(option: any) => String(option.value)}
                  autoFocus
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
